import { AgGridReact } from "ag-grid-react";
import { AgGridReact as AgGridType } from "ag-grid-react";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload, FiFilter, FiRefreshCw, FiSearch } from "react-icons/fi";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import AgGrid from "../../../components/grid/AgGrid";
import {
  centerAlignColDef,
  defaultColDef,
  floatFormater,
  leftAlignColDef,
  rightAlignColDef,
} from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import SearchBox from "../../../components/input/SearchBox";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import ExportAsXLSXButton from "../../../components/export/ExportAsXLSXButton";
import CheckBox from "../../../components/input/CheckBox";
import RefreshButton from "../../../components/button/RefreshButton";

const ReportGrid = ({ companies, title, selectedCompanyIds, setSelectedCompanyIds, globalView, setGlobalView }) => {
  const { t } = useTranslation();
  const table_ref = useRef();
  const header_ref = useRef();
  const { columnVisibilities, setRefresh } = useContextProvider();
  const colVisibility_key = "supplier_directory_dashboard_company_table";
  const [showColVisibilityMenu, setShowColVisibilityMenu] = useState(false);
  const columnDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
    const colDefs = [
      { field: "name", headerName: t("Name"), ...leftAlignColDef, ...defaultColDef },
      { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      {
        field: "cost_per_gwp",
        headerName: t("Cost/GWP"),
        ...rightAlignColDef,
        ...defaultColDef,
        valueGetter: (params) => {
          if (!params.data || !params.data.gwp || params.data.gwp === 0) return null;
          return Number(params.data.cost || 0) / Number(params.data.gwp);
        },
        valueFormatter: (params) => {
          if (params.value == null) return "—";
          return params.value.toFixed(4);
        },
      },
      {
        field: "report_count",
        headerName: t("Submissions"),
        ...centerAlignColDef,
        ...defaultColDef,
        valueGetter: (params) => params.data?.reports?.length || 0,
      },
      {
        field: "avg_gwp_per_unit",
        headerName: t("Avg GWP/Unit"),
        ...rightAlignColDef,
        ...defaultColDef,
        valueGetter: (params) => {
          const reports = params.data?.reports || [];
          if (reports.length === 0) return null;
          const total = reports.reduce((sum, r) => sum + (Number(r.gwp_per_unit) || 0), 0);
          return total / reports.length;
        },
        valueFormatter: (params) => {
          if (params.value == null) return "—";
          return params.value.toFixed(2);
        },
      },
      {
        field: "total_quantity",
        headerName: t("Quantity"),
        ...rightAlignColDef,
        ...defaultColDef,
        valueGetter: (params) => {
          const reports = params.data?.reports || [];
          return reports.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
        },
        valueFormatter: (params) => {
          if (!params.value) return "—";
          return params.value.toLocaleString();
        },
      },
      {
        field: "last_submitted",
        headerName: t("Last Submitted"),
        ...rightAlignColDef,
        ...defaultColDef,
        valueGetter: (params) => {
          const reports = params.data?.reports || [];
          if (reports.length === 0) return null;
          const dates = reports.map((r) => r.created_at || r.last_modified).filter(Boolean);
          if (dates.length === 0) return null;
          return dates.sort().reverse()[0];
        },
        valueFormatter: (params) => {
          if (!params.value) return "—";
          try {
            return new Date(params.value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
          } catch {
            return params.value;
          }
        },
      },
    ];
    return colDefs.map((col) => {
      return { ...col, hide: isHidden(col.field) };
    });
  }, [columnVisibilities, t]);

  const totalMetrics = useMemo(() => {
    const totalGwp = (companies || []).reduce((sum, c) => sum + (Number(c.gwp) || 0), 0);
    const totalCost = (companies || []).reduce((sum, c) => sum + (Number(c.cost) || 0), 0);
    return { 
      gwp: totalGwp.toLocaleString(undefined, { maximumFractionDigits: 0 }), 
      cost: totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 }) 
    };
  }, [companies]);

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (table_ref.current?.api) {
      table_ref.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  useEffect(() => {
    if (table_ref.current?.api && selectedCompanyIds && selectedCompanyIds.length === 0) {
      table_ref.current.api.deselectAll();
    }
  }, [selectedCompanyIds]);

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {t(title)}
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer ref={header_ref}>
            <Box sx={{ width: 450 }}>
              <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search suppliers...")} width={450} />
            </Box>
            <ColumnVisibilityButton onClick={() => setShowColVisibilityMenu((p) => !p)} />
            <RefreshButton setRefresh={setRefresh} />
            <ExportAsXLSXButton data={companies} label="Export as .xlsx" filename="suppliers.xlsx" />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <AgGrid
            ref={table_ref}
            rowData={companies}
            columnDefs={columnDefs}
            onSelectionChanged={(event) => {
              setSelectedCompanyIds(event.api.getSelectedRows().map((r) => r.id));
            }}
            domLayout="autoHeight"
            footerLeftContent={
              setGlobalView && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                  <CheckBox
                    checked={globalView}
                    onChange={(e) => setGlobalView(e.target.checked)}
                    label={t("Global View")}
                  />
                </Box>
              )
            }
            footerRightContent={<Box sx={{ width: 10 }} />}
          />
        </Box>
      </Paper>
      <ColumnMenu
        anchorRef={header_ref}
        visible={showColVisibilityMenu}
        onClose={() => setShowColVisibilityMenu(false)}
        colDefs={columnDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={["name", "cost", "gwp", "cost_per_gwp", "report_count", "avg_gwp_per_unit", "total_quantity", "last_submitted"]}
      />
    </Box>
  );
};

export default ReportGrid;
